/**
 * Client-side enterprise theme utilities
 * Use these in Client Components
 *
 * NOTE: These functions return default values when themes are not in cache.
 * For server-side usage, use @/utils/enterprise-theme-server instead.
 */

import type { FontFamilyConfig, TenantConfig } from "./enterprise-theme-types";

export type { OrganizationInfo, TenantConfig } from "./enterprise-theme-types";

export const DEFAULT_FONT_FAMILY = '"Satoshi Variable", "Satoshi", sans-serif';

// Core Utilities
export const getEnterpriseSlugFromHostname = (hostname: string): string | undefined => {
  // Match production domains (*.nowadays.ai)
  const subdomainMatch = hostname.match(/^([^.]+)\.nowadays\.ai$/);
  if (subdomainMatch) {
    const subdomain = subdomainMatch[1];
    if (subdomain !== "www" && subdomain !== "app") {
      return subdomain;
    }
  }

  // Match localhost domains (*.localhost:3000 or *.localhost)
  const localhostMatch = hostname.match(/^([^.]+)\.localhost(:\d+)?$/);
  if (localhostMatch) {
    const subdomain = localhostMatch[1];
    if (subdomain !== "www" && subdomain !== "app") {
      return subdomain;
    }
  }

  return undefined;
};

export const getEnterpriseSlugFromUrl = (): string | undefined => {
  if (typeof window !== "undefined") {
    return getEnterpriseSlugFromHostname(window.location.hostname);
  }

  return undefined;
};

// Helper to get CSS variable value
function getCSSVariable(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || undefined;
}

// Font Functions
export const getFontFamilyForEnterprise = (): FontFamilyConfig | undefined => {
  const fontFamily = getCSSVariable("--font-family-primary");
  if (!fontFamily) return undefined;
  return fontFamily.split(",").map((f) => f.trim());
};

export const getPrimaryFontFamily = (): string => {
  return getCSSVariable("--font-family-primary") || DEFAULT_FONT_FAMILY;
};

// Tenant Config Functions (synchronous, returns defaults)
export const getTenantConfigSync = (siteSlug: string): TenantConfig => {
  // Return config with no theme - for client-side usage
  return {
    slug: siteSlug,
    indigoColors: undefined,
    fontFamily: undefined,
    logoUrl: undefined,
  };
};

// Helper Utilities

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 82, g: 97, b: 235 };
};
